import { requestApi } from '@app/api';
import { withDocument } from '@app/api/paths';
import splitToSubstrByLastDelimiter from '@app/libs/splitToSubstrByLastDelimiter';
import { CarbonCopyContact } from '@app/store/contract-setup/reducers/carbonCopies';
import { HostReference } from '@app/store/Host';
import { convertTransportHostsToAppHosts } from '@app/store/transport-layer/lib/host';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { Field as TransportLayerField } from '@app/store/transport-layer/types/field';
import { FieldGroup as TransportLayerFieldGroup } from '@app/store/transport-layer/types/fieldGroup';
import {
  HostWithSigningProperties,
  Locale,
  ParticipantWithSigningProperties,
  Requester,
  SignerWithSigningProperties,
} from '@app/store/transport-layer/types/participant';
import {
  DcertVerificationMethod,
  MobileIdentVerificationMethod,
  Verification as TransportLayerVerification,
} from '@app/store/transport-layer/types/verification';

interface Size {
  height: number;
  width: number;
}
interface Position {
  x: number;
  y: number;
  page: number;
}

interface RequesterInputPosition {
  x: number;
  y: number;
  z: number;
  page: number;
}

interface TextStyle {
  size: number;
  font: 'Noto Sans' | 'Noto Serif';
  align: 'LEFT' | 'CENTER' | 'RIGHT';
}

export interface ParticipantCheckboxField {
  type: 'CHECKBOX';
  dataLabel: string;
  value: boolean;
  required: boolean;
  size: Size;
  position: Position;
  groupLabel: string | null;
}

export interface ParticipantCheckboxGroup {
  groupLabel: string;
  minimumRequired: number;
  maximumAllowed: number;
  checkboxNumber: number;
}

export interface ParticipantTextField {
  type: 'TEXT';
  dataLabel?: string;
  value: string;
  required: boolean;
  size: Size;
  position: Position;
  textStyle: TextStyle;
}

export interface ParticipantSignatureField {
  type: 'SIGNATURE';
  dataLabel?: string;
  signatureTypes: Array<'STAMP' | 'SIGN'>;
  size: Size;
  position: Position;
}

export type ParticipantField =
  | ParticipantCheckboxField
  | ParticipantTextField
  | ParticipantSignatureField;

interface RequesterCheckboxField {
  type: 'CHECKBOX';
  dataLabel?: string;
  value: boolean;
  size: Size;
  position: RequesterInputPosition;
}

interface RequesterImageField {
  type: 'IMAGE';
  dataLabel?: string;
  value: string;
  size: Size;
  position: RequesterInputPosition;
}

interface RequesterTextField {
  type: 'TEXT';
  dataLabel?: string;
  size: Size;
  value: string;
  position: RequesterInputPosition;
  textStyle: TextStyle;
}

export type RequesterField = RequesterCheckboxField | RequesterImageField | RequesterTextField;

interface Attachment {
  name: string;
  dataLabel?: string;
  message: string | null;
  required: boolean;
}

interface Verification {
  password?: {
    value: string;
  };
  mobileIdentification?: {
    name: string;
    phoneNumber: string;
  };
  dCert?: {
    name: string;
    bizNumber: string;
  };
}
export interface Participant {
  name?: string | null;
  requesterMessage?: string;
  locale?: string;
  role?: string;
  signingOrder?: number;
  signingDuration?: number;
  signingMethod?: {
    type?: 'EMAIL' | 'KAKAO' | 'SECURE_LINK' | 'IN_PERSON';
    value?: string;
  };
  attachmentRequests?: Array<Attachment>;
  verification?: Verification;
  fieldGroups?: TransportLayerFieldGroup[];
  fields?: TransportLayerField['ToReceive'][];
  host?: HostReference;
}

export interface Host {
  name: string;
  contactInfo: string;
  dataLabel: string;
  locale: Locale;
}

export interface GetDraftByIdResponse {
  title?: string;
  carbonCopies?: Array<CarbonCopyContact>;
  file?: { downloadUrl: string; fileId: string };
  fileOpenPassword?: string;
  participants?: Array<Participant>;
  hosts?: Array<Host>;
  requesterInputs?: Array<RequesterField>;
  keywords?: Array<string>;
}

export interface NewGetDraftByIdResponse {
  id: string;
  carbonCopies: Array<CarbonCopyContact>;
  file: { url: string; fileId: string } | null;
  title: string | null;
  pdfPassword: string | null;
  participants: ParticipantWithSigningProperties[];
  attachments: TransportLayerAttachment['ToReceive'][];
  fields: TransportLayerField['ToReceive'][];
  fieldGroups: TransportLayerFieldGroup[];
  verifications: TransportLayerVerification[];
  userId: string;
  createdAt: string;
  keywords?: Array<string>;
}

const convertToDeprecatedResponse = (response: NewGetDraftByIdResponse): GetDraftByIdResponse => {
  const mapperForParticipant = (signer: SignerWithSigningProperties): Participant => {
    const verifications = response.verifications.find(
      (v) => v.participant.dataLabel === signer.dataLabel
    );

    const passwordVerification = verifications?.methods.find((v) => v.type === 'PASSWORD');
    const mobileIdentification = verifications?.methods.find(
      (v) => v.type === 'MOBILE_IDENTIFICATION'
    ) as MobileIdentVerificationMethod | undefined;
    const dCert = verifications?.methods.find((v) => v.type === 'D_CERT') as
      | DcertVerificationMethod
      | undefined;

    return {
      name: signer.name,
      requesterMessage: signer.message || undefined,
      locale: signer.locale,
      role: signer.role,
      signingOrder: signer.order,
      signingDuration: signer.signingDuration,
      signingMethod: {
        type: signer.signingMethod as 'EMAIL' | 'KAKAO' | 'IN_PERSON',
        value: signer.contact,
      },
      attachmentRequests: response.attachments
        .filter((a) => a.participant.dataLabel === signer.dataLabel)
        .map<Attachment>((attachment) => {
          return {
            name: attachment.title,
            message: attachment.message,
            required: attachment.config.required,
          };
        }),
      verification: {
        password: passwordVerification
          ? {
              value: passwordVerification.passValue,
            }
          : undefined,
        mobileIdentification: mobileIdentification
          ? {
              name: mobileIdentification.displayName || '',
              phoneNumber:
                splitToSubstrByLastDelimiter(mobileIdentification.passValue, '|')[1] || '',
            }
          : undefined,
        dCert: dCert
          ? {
              name: dCert.displayName || '',
              bizNumber: splitToSubstrByLastDelimiter(dCert.passValue, '|')[1] || '',
            }
          : undefined,
      },
      fields: response.fields.filter((f) => f.participant.dataLabel === signer.dataLabel),
      fieldGroups: response.fieldGroups.filter(
        (fg) => fg.participant.dataLabel === signer.dataLabel
      ),
      host: signer.host,
    };
  };

  const requesterDataLabel = response.participants.filter(
    (p): p is Requester => p.type === 'REQUESTER'
  )[0].dataLabel;

  return {
    title: response.title || undefined,
    carbonCopies: response.carbonCopies || [],
    fileOpenPassword: response.pdfPassword || undefined,
    file: response.file
      ? {
          downloadUrl: response.file.url,
          fileId: response.file.fileId,
        }
      : undefined,
    keywords: response.keywords,
    hosts: convertTransportHostsToAppHosts(
      response.participants.filter((p): p is HostWithSigningProperties => p.type === 'HOST')
    ),
    participants: response.participants
      .filter((p): p is SignerWithSigningProperties => p.type === 'SIGNER')
      .map<Participant>(mapperForParticipant),
    requesterInputs: response.fields
      .filter((f) => f.participant.dataLabel === requesterDataLabel)
      .map<RequesterField>((field) => {
        const result = {
          dataLabel: field.dataLabel,
          size: {
            width: field.guide.size.width,
            height: field.guide.size.height,
          },
          position: {
            x: field.guide.position.x,
            y: field.guide.position.y,
            z: field.guide.position.z,
            page: field.guide.position.page,
          },
        };

        switch (field.type) {
          case 'CHECKBOX':
            return {
              ...result,
              type: 'CHECKBOX',
              value: field.input ? field.input.value === 'TRUE' : false,
            };
          case 'TEXT':
            return {
              ...result,
              type: 'TEXT',
              value: field.input?.value || '',
              textStyle: {
                size: field.config.fontStyle.size,
                font: field.config.fontStyle.type === 'NOTO_SANS' ? 'Noto Sans' : 'Noto Serif',
                align: field.config.textAlign,
              },
            };
          case 'IMAGE':
            return {
              ...result,
              type: 'IMAGE',
              value: field.input?.value || '',
            };
          default:
            throw new Error(`The field type ${field.type} is not supported for requester-input`);
        }
      }),
  };
};

export function getDraftById(draftId: string): Promise<GetDraftByIdResponse> {
  return getNewDraftById(draftId).then((data) => convertToDeprecatedResponse(data));
}

export function getNewDraftById(draftId: string) {
  return requestApi<NewGetDraftByIdResponse>({
    url: withDocument(`/drafts/${draftId}`),
    method: 'GET',
    credentials: true,
  }).then((res) => res.data);
}
